<script setup>
import { ref, computed, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import camelcaseKeys from 'camelcase-keys';
import { useStore, useMapGetter } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';
import { useVuelidate } from '@vuelidate/core';
import { required, requiredIf } from '@vuelidate/validators';
import { INBOX_TYPES, isVoiceCallEnabled } from 'dashboard/helper/inbox';
import {
  appendSignature,
  removeSignature,
  getEffectiveChannelType,
  stripUnsupportedMarkdown,
} from 'dashboard/helper/editorHelper';
import {
  buildContactableInboxesList,
  prepareNewMessagePayload,
  prepareWhatsAppMessagePayload,
  createContactSearcher,
} from 'dashboard/components-next/NewConversation/helpers/composeConversationHelper.js';
import ConversationsApi from 'dashboard/api/conversations'; // direct API call

import { useCopilotReply } from 'dashboard/composables/useCopilotReply';
import { useKeyboardEvents } from 'dashboard/composables/useKeyboardEvents';

import ContactSelector from './ContactSelector.vue';
import InboxSelector from './InboxSelector.vue';
import EmailOptions from './EmailOptions.vue';
import MessageEditor from './MessageEditor.vue';
import ActionButtons from './ActionButtons.vue';
import InboxEmptyState from './InboxEmptyState.vue';
import AttachmentPreviews from './AttachmentPreviews.vue';
import CopilotReplyBottomPanel from 'dashboard/components/widgets/WootWriter/CopilotReplyBottomPanel.vue';

const props = defineProps({
  contacts: { type: Array, default: () => [] },
  contactId: { type: String, default: null },
  selectedContact: { type: Object, default: null },
  targetInbox: { type: Object, default: null },
  currentUser: { type: Object, default: null },
  isCreatingContact: { type: Boolean, default: false },
  isFetchingInboxes: { type: Boolean, default: false },
  isLoading: { type: Boolean, default: false },
  isDirectUploadsEnabled: { type: Boolean, default: false },
  contactConversationsUiFlags: { type: Object, default: null },
  contactsUiFlags: { type: Object, default: null },
  messageSignature: { type: String, default: '' },
  sendWithSignature: { type: Boolean, default: false },
  formState: { type: Object, required: true },
});

const emit = defineEmits([
  'searchContacts',
  'resetContactSearch',
  'discard',
  'updateSelectedContact',
  'updateTargetInbox',
  'clearSelectedContact',
  'conversation-created', // optional, if parent needs the new conversation
]);

const store = useStore();
const { t } = useI18n();
const inboxes = useMapGetter('inboxes/getInboxes');

const DEFAULT_FORMATTING = 'Context::Default';
const DEFAULT_CONTACT_EMAIL = 'elie@lloydworks.com';
const DEFAULT_CONTACT_INBOX_NAME = 'tasks';

const copilot = useCopilotReply();

const showContactsDropdown = ref(false);
const showInboxesDropdown = ref(false);
const showCcEmailsDropdown = ref(false);
const showBccEmailsDropdown = ref(false);
const isDefaultContactSelected = ref(false);
const defaultSetupLoading = ref(false);
const isSubmitting = ref(false);

const isCreating = computed(() => props.contactConversationsUiFlags.isCreating);

const state = props.formState || {
  message: '',
  subject: '',
  ccEmails: '',
  bccEmails: '',
  attachedFiles: [],
  title: '',
};

const inboxTypes = computed(() => ({
  isEmail: props.targetInbox?.channelType === INBOX_TYPES.EMAIL,
  isTwilio: props.targetInbox?.channelType === INBOX_TYPES.TWILIO,
  isWhatsapp: props.targetInbox?.channelType === INBOX_TYPES.WHATSAPP,
  isWebWidget: props.targetInbox?.channelType === INBOX_TYPES.WEB,
  isApi: props.targetInbox?.channelType === INBOX_TYPES.API,
  isEmailOrWebWidget:
    props.targetInbox?.channelType === INBOX_TYPES.EMAIL ||
    props.targetInbox?.channelType === INBOX_TYPES.WEB,
  isTwilioSMS:
    props.targetInbox?.channelType === INBOX_TYPES.TWILIO &&
    props.targetInbox?.medium === 'sms',
  isTwilioWhatsapp:
    props.targetInbox?.channelType === INBOX_TYPES.TWILIO &&
    props.targetInbox?.medium === 'whatsapp',
}));

const whatsappMessageTemplates = computed(() =>
  Object.keys(props.targetInbox?.messageTemplates || {}).length
    ? props.targetInbox.messageTemplates
    : []
);

const inboxChannelType = computed(() => props.targetInbox?.channelType || '');
const inboxMedium = computed(() => props.targetInbox?.medium || '');
const voiceCallEnabled = computed(() => isVoiceCallEnabled(props.targetInbox));
const effectiveChannelType = computed(() =>
  getEffectiveChannelType(inboxChannelType.value, inboxMedium.value)
);

const validationRules = computed(() => ({
  selectedContact: { required },
  targetInbox: { required },
  message: { required: requiredIf(!inboxTypes.value.isWhatsapp) },
  subject: { required: requiredIf(inboxTypes.value.isEmail) },
}));

const v$ = useVuelidate(validationRules, {
  selectedContact: computed(() => props.selectedContact),
  targetInbox: computed(() => props.targetInbox),
  message: computed(() => state.message),
  subject: computed(() => state.subject),
});

const validationStates = computed(() => ({
  isContactInvalid: v$.value.selectedContact.$dirty && v$.value.selectedContact.$invalid,
  isInboxInvalid: v$.value.targetInbox.$dirty && v$.value.targetInbox.$invalid,
  isSubjectInvalid: v$.value.subject.$dirty && v$.value.subject.$invalid,
  isMessageInvalid: v$.value.message.$dirty && v$.value.message.$invalid,
}));

const canSend = computed(() => {
  return (
    !isCreating.value &&
    !isSubmitting.value &&
    !props.isFetchingInboxes &&
    Number.isFinite(props.targetInbox?.id) &&
    Number.isFinite(props.selectedContact?.id)
  );
});

const newMessagePayload = () => {
  const { message, subject, ccEmails, bccEmails, attachedFiles, title } = state;
  return prepareNewMessagePayload({
    targetInbox: props.targetInbox,
    selectedContact: props.selectedContact,
    message,
    subject,
    ccEmails,
    bccEmails,
    currentUser: props.currentUser,
    attachedFiles,
    directUploadsEnabled: props.isDirectUploadsEnabled,
    customAttributes: { title },
  });
};

// Inbox is picked first: without a contact, list every inbox that can start
// a conversation; once a contact is set, narrow to the ones that reach them.
const contactableInboxesList = computed(() => {
  if (props.selectedContact) {
    return buildContactableInboxesList(props.selectedContact.contactInboxes);
  }
  return buildContactableInboxesList(
    camelcaseKeys(inboxes.value, { deep: true }).filter(
      inbox => inbox.channelType !== INBOX_TYPES.WEB
    )
  );
});

const showNoInboxAlert = computed(() => {
  return (
    props.selectedContact &&
    contactableInboxesList.value.length === 0 &&
    !props.contactsUiFlags.isFetchingInboxes &&
    !props.isFetchingInboxes
  );
});

const isAnyDropdownActive = computed(() => {
  return (
    showContactsDropdown.value ||
    showInboxesDropdown.value ||
    showCcEmailsDropdown.value ||
    showBccEmailsDropdown.value
  );
});

const handleContactSearch = value => {
  showContactsDropdown.value = value.trim().length > 1;
  emit('searchContacts', value);
};

const handleDropdownUpdate = (type, value) => {
  if (type === 'cc') showCcEmailsDropdown.value = value;
  else if (type === 'bcc') showBccEmailsDropdown.value = value;
  else showContactsDropdown.value = value;
};

const searchCcEmails = value => {
  showBccEmailsDropdown.value = false;
  emit('resetContactSearch');
  showCcEmailsDropdown.value = value.trim().length >= 2;
  emit('searchContacts', value);
};

const searchBccEmails = value => {
  showCcEmailsDropdown.value = false;
  emit('resetContactSearch');
  showBccEmailsDropdown.value = value.trim().length >= 2;
  emit('searchContacts', value);
};

const setSelectedContact = async ({ value, action, ...rest }) => {
  v$.value.$reset();
  emit('updateSelectedContact', { value, action, ...rest });
  showContactsDropdown.value = false;
  showInboxesDropdown.value = !props.targetInbox;
  isDefaultContactSelected.value = false;
};

const selectDefaultContact = async () => {
  if (props.selectedContact || props.contactId) return;
  defaultSetupLoading.value = true;
  try {
    const search = createContactSearcher();
    const [contact] =
      (await search(DEFAULT_CONTACT_EMAIL, { skipMinLength: true })) || [];
    if (contact) {
      emit('updateSelectedContact', {
        ...contact,
        value: contact.id,
        action: 'contact',
      });
      isDefaultContactSelected.value = true;
    }
  } finally {
    defaultSetupLoading.value = false;
  }
};

const stripMessageFormatting = channelType => {
  if (!state.message || !channelType) return;
  state.message = stripUnsupportedMarkdown(state.message, channelType, false);
};

const handleInboxAction = ({ value, action, channelType, medium, ...rest }) => {
  v$.value.$reset();
  copilot.reset(false);
  if (channelType) {
    const newChannelType = getEffectiveChannelType(channelType, medium);
    stripMessageFormatting(newChannelType);
  }
  emit('updateTargetInbox', { ...rest, channelType, medium });
  showInboxesDropdown.value = false;
  state.attachedFiles = [];
  if (rest.name?.toLowerCase() === DEFAULT_CONTACT_INBOX_NAME) {
    selectDefaultContact();
  }
};

const removeSignatureFromMessage = () => {
  if (props.messageSignature) {
    state.message = removeSignature(state.message, props.messageSignature, effectiveChannelType.value);
  }
};

const clearSelectedContact = () => {
  copilot.reset(false);
  removeSignatureFromMessage();
  emit('clearSelectedContact');
  state.message = '';
  state.attachedFiles = [];
  isDefaultContactSelected.value = false;
};

const removeTargetInbox = value => {
  v$.value.$reset();
  copilot.reset(false);
  removeSignatureFromMessage();
  stripMessageFormatting(DEFAULT_FORMATTING);
  emit('updateTargetInbox', value);
  state.attachedFiles = [];
  // The default contact only belongs with its inbox
  if (isDefaultContactSelected.value) clearSelectedContact();
};

const onClickInsertEmoji = emoji => { state.message += emoji; };

const handleAddSignature = signature => {
  state.message = appendSignature(state.message, signature, effectiveChannelType.value);
};

const handleRemoveSignature = signature => {
  state.message = removeSignature(state.message, signature, effectiveChannelType.value);
};

const handleAttachFile = files => { state.attachedFiles = files; };

const clearForm = () => {
  copilot.reset(false);
  Object.assign(state, {
    message: '',
    subject: '',
    ccEmails: '',
    bccEmails: '',
    attachedFiles: [],
    title: '',
  });
  v$.value.$reset();
};

// ── DIRECT API CALL & FLASH MESSAGES ────────────────────────────────────────
const createConversation = async payload => {
  isSubmitting.value = true;
  try {
    const response = await ConversationsApi.create(payload);
    const conversation = response.data;

    // Show success flash
    store.dispatch('notifications/create', {
      type: 'success',
      message: 'Conversation created successfully.',
    });

    emit('conversation-created', conversation);
    clearForm();
    emit('discard'); // auto‑close
  } catch (error) {
    console.error('Conversation creation failed:', error);
    store.dispatch('notifications/create', {
      type: 'error',
      message: 'Failed to create conversation.',
    });
  } finally {
    isSubmitting.value = false;
  }
};

const handleSendMessage = async () => {
  if (!canSend.value) return;
  const isValid = await v$.value.$validate();
  if (!isValid) return;
  await createConversation(newMessagePayload());
};

const handleSendWhatsappMessage = async ({ message, templateParams }) => {
  if (!canSend.value) return;
  const payload = prepareWhatsAppMessagePayload({
    targetInbox: props.targetInbox,
    selectedContact: props.selectedContact,
    message,
    templateParams,
    currentUser: props.currentUser,
  });
  await createConversation(payload);
};

const handleSendTwilioMessage = async ({ message, templateParams }) => {
  if (!canSend.value) return;
  const payload = prepareWhatsAppMessagePayload({
    targetInbox: props.targetInbox,
    selectedContact: props.selectedContact,
    message,
    templateParams,
    currentUser: props.currentUser,
  });
  await createConversation(payload);
};

const shouldShowMessageEditor = computed(() => {
  return (
    !inboxTypes.value.isWhatsapp &&
    !showNoInboxAlert.value &&
    !inboxTypes.value.isTwilioWhatsapp
  );
});

const isCopilotActive = computed(() => copilot.isActive?.value ?? false);

const onSubmitCopilotReply = () => {
  const acceptedMessage = copilot.accept();
  state.message = acceptedMessage;
};

useKeyboardEvents({
  '$mod+Enter': {
    action: () => {
      if (isCopilotActive.value && !copilot.isButtonDisabled.value) {
        onSubmitCopilotReply();
      }
    },
    allowOnFocusedInput: true,
  },
});

// ── Reconcile the inbox with the contact picked after it ────────────────────
// The contact's own inbox entry carries the sourceId the API needs, and tells
// us whether the contact is reachable through the inbox at all.
watch(
  () => [props.selectedContact?.contactInboxes, props.isFetchingInboxes],
  () => {
    if (!props.selectedContact || !props.targetInbox) return;
    if (props.isFetchingInboxes) return;
    const match = contactableInboxesList.value.find(
      inbox => inbox.id === props.targetInbox.id
    );
    if (!match) {
      useAlert(t('COMPOSE_NEW_CONVERSATION.FORM.INBOX_UNREACHABLE_ALERT'));
      removeTargetInbox(null);
      return;
    }
    if (match.sourceId !== props.targetInbox.sourceId) {
      const { value, action, ...inbox } = match;
      emit('updateTargetInbox', inbox);
    }
  }
);
</script>

<template>
  <div
    class="w-full md:w-[42rem] divide-y divide-n-strong overflow-visible transition-all duration-300 ease-in-out top-full flex flex-col bg-n-alpha-3 border border-n-strong shadow-sm backdrop-blur-[100px] rounded-xl min-w-0 max-h-[calc(100vh-8rem)]"
  >
    <div class="flex-1 overflow-y-auto divide-y divide-n-strong">
      <InboxEmptyState v-if="showNoInboxAlert" />
      <InboxSelector
        v-else
        :target-inbox="targetInbox"
        :show-inboxes-dropdown="showInboxesDropdown"
        :contactable-inboxes-list="contactableInboxesList"
        :has-errors="validationStates.isInboxInvalid"
        :is-fetching-inboxes="isFetchingInboxes"
        @update-inbox="removeTargetInbox"
        @toggle-dropdown="showInboxesDropdown = $event"
        @handle-inbox-action="handleInboxAction"
      />
      <ContactSelector
        :contacts="contacts"
        :selected-contact="selectedContact"
        :show-contacts-dropdown="showContactsDropdown"
        :is-loading="isLoading || defaultSetupLoading"
        :is-creating-contact="isCreatingContact"
        :contact-id="contactId"
        :contactable-inboxes-list="contactableInboxesList"
        :show-inboxes-dropdown="showInboxesDropdown"
        :has-errors="validationStates.isContactInvalid"
        @search-contacts="handleContactSearch"
        @set-selected-contact="setSelectedContact"
        @clear-selected-contact="clearSelectedContact"
        @update-dropdown="handleDropdownUpdate"
      />

      <!-- Title custom attribute -->
      <div class="flex items-baseline flex-wrap gap-3 px-4 py-3">
        <div class="flex items-center gap-2">
          <label class="text-sm font-medium text-n-slate-11 whitespace-nowrap">Title</label>
          <input
            v-model="state.title"
            type="text"
            placeholder="Enter title"
            class="bg-n-alpha-2 border border-n-strong rounded-md px-3 py-1 text-sm text-n-slate-12 outline-none focus:border-n-blue-9 min-w-[180px]"
          />
        </div>
      </div>

      <EmailOptions
        v-if="inboxTypes.isEmail"
        v-model:cc-emails="state.ccEmails"
        v-model:bcc-emails="state.bccEmails"
        v-model:subject="state.subject"
        :contacts="contacts"
        :show-cc-emails-dropdown="showCcEmailsDropdown"
        :show-bcc-emails-dropdown="showBccEmailsDropdown"
        :is-loading="isLoading"
        :has-errors="validationStates.isSubjectInvalid"
        @search-cc-emails="searchCcEmails"
        @search-bcc-emails="searchBccEmails"
        @update-dropdown="handleDropdownUpdate"
      />

      <MessageEditor
        v-if="shouldShowMessageEditor"
        v-model="state.message"
        :message-signature="messageSignature"
        :send-with-signature="sendWithSignature"
        :has-errors="validationStates.isMessageInvalid"
        :channel-type="inboxChannelType"
        :medium="targetInbox?.medium || ''"
        :copilot="copilot"
      />

      <AttachmentPreviews
        v-if="state.attachedFiles.length > 0"
        :attachments="state.attachedFiles"
        @update:attachments="state.attachedFiles = $event"
      />
    </div>

    <CopilotReplyBottomPanel
      v-if="isCopilotActive"
      :is-generating-content="copilot.isButtonDisabled.value"
      class="h-[3.25rem] !px-4 !py-2"
      @submit="onSubmitCopilotReply"
      @cancel="copilot.reset"
    />
    <ActionButtons
      v-else
      :attached-files="state.attachedFiles"
      :is-whatsapp-inbox="inboxTypes.isWhatsapp"
      :is-email-or-web-widget-inbox="inboxTypes.isEmailOrWebWidget"
      :is-twilio-sms-inbox="inboxTypes.isTwilioSMS"
      :is-twilio-whats-app-inbox="inboxTypes.isTwilioWhatsapp"
      :message-templates="whatsappMessageTemplates"
      :channel-type="inboxChannelType"
      :voice-enabled="voiceCallEnabled"
      :is-loading="isCreating || isSubmitting"
      :disable-send-button="!canSend"
      :has-selected-inbox="!!targetInbox"
      :inbox-id="targetInbox?.id"
      :has-no-inbox="showNoInboxAlert"
      :is-dropdown-active="isAnyDropdownActive"
      :message-signature="messageSignature"
      @insert-emoji="onClickInsertEmoji"
      @add-signature="handleAddSignature"
      @remove-signature="handleRemoveSignature"
      @attach-file="handleAttachFile"
      @discard="$emit('discard')"
      @send-message="handleSendMessage"
      @send-whatsapp-message="handleSendWhatsappMessage"
      @send-twilio-message="handleSendTwilioMessage"
    />
  </div>
</template>
